// src/components/legal/CryptCrawlerAccountDeletion.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #9B2C2C;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #9B2C2C;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '←';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const WarningBox = styled.div`
    background: #FFEBEE;
    border-left: 4px solid #F44336;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const InfoBox = styled.div`
    background: #FBEDED;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const StepBox = styled.div`
    background: #F5F5F5;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 1rem 0;

    h3 {
        color: #9B2C2C;
        margin-bottom: 0.5rem;
    }
`;

const ContactBox = styled.div`
    background: #F6E1E1;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
`;

const CryptCrawlerAccountDeletion = () => {
    return (
        <Container>
            <BackLink to="/cryptcrawler">CryptCrawler 홈으로 / Back to CryptCrawler</BackLink>

            <Header>
                <Title>CryptCrawler Data Deletion / 데이터 삭제 안내</Title>
                <UpdateDate>Last updated / 최종 수정일: October 13, 2026</UpdateDate>
            </Header>

            <WarningBox>
                <strong>Notice / 안내:</strong> CryptCrawler has no account registration or login, and there is no account to delete. All game progress is stored only on your device. The only data held outside your device is advertising and purchase data processed by Google AdMob and the platform store, which you can reset or request deletion of as described below.
                <br /><br />
                CryptCrawler는 회원가입·로그인이 없어 삭제할 계정이 없으며, 모든 게임 진행 정보는 기기에만 저장됩니다. 기기 밖에는 Google AdMob과 플랫폼 스토어가 처리하는 광고·결제 데이터만 있으며, 아래 방법으로 초기화하거나 삭제를 요청할 수 있습니다.
            </WarningBox>

            <Section>
                <SectionTitle>How to Delete Your Data / 데이터 삭제 방법</SectionTitle>

                <StepBox>
                    <h3>Method 1: Uninstall the app or clear app data / 앱 삭제 또는 앱 데이터 삭제</h3>
                    <Paragraph>
                        Uninstalling CryptCrawler, or clearing its app data, permanently removes all locally stored progress: unlocks, card mastery, achievements, statistics, platinum and gold, the current run, settings, and local save backups.

                        Steps (Android):
                        1. Settings → Apps → CryptCrawler
                        2. Tap "Storage" → "Clear data", or tap "Uninstall"
                        3. Confirm

                        앱을 삭제하거나 앱 데이터를 지우면 기기에 저장된 해금·숙련도·업적·통계·백금·골드·진행 중인 런·설정·로컬 백업이 모두 영구 삭제됩니다.
                    </Paragraph>
                </StepBox>

                <StepBox>
                    <h3>Method 2: Reset Advertising ID / 광고 ID 초기화</h3>
                    <Paragraph>
                        To break the link between you and advertising data already collected by Google AdMob, reset or delete your device Advertising ID:

                        Android: Settings → Google → Ads → "Delete advertising ID" (or "Reset advertising ID")
                        iOS: Settings → Privacy & Security → Tracking / Apple Advertising

                        After reset, future data is associated with a new identifier and is no longer linkable to your old data.

                        광고 ID 초기화: 기기 설정 → Google → 광고 → "광고 ID 삭제"(Android) 또는 iOS의 추적/광고 설정에서 초기화하면 이후 데이터는 새 식별자와 연결됩니다.
                    </Paragraph>
                </StepBox>

                <StepBox>
                    <h3>Method 3: Email Request / 이메일 요청 (GDPR Article 17, PIPA Art. 36)</h3>
                    <Paragraph>
                        To request deletion of any data we hold or can request from our processors (advertising records, purchase records we hold, support correspondence), email us:

                        Send to: mind-wellness@naver.com
                        Subject: [Data Deletion Request] CryptCrawler

                        Include any of the following you have available:
                        • Your Advertising ID (Android: Settings → Google → Ads)
                        • Order ID from the platform store (if you made a purchase)
                        • Approximate install date and country
                        • Reason for deletion (optional)

                        Processing time: We will confirm receipt within 5 business days and complete the request within 30 days.

                        삭제 요청: 위 정보를 포함하여 mind-wellness@naver.com으로 보내주시면 30일 이내 처리됩니다.
                    </Paragraph>
                </StepBox>
            </Section>

            <Section>
                <SectionTitle>Data That Will Be Deleted / 삭제되는 데이터</SectionTitle>
                <Paragraph>
                    <strong>On your device (uninstall / clear data):</strong>
                    • All game progress, currencies, run state, settings, and local backups

                    <strong>On request:</strong>
                    • Customer support emails and any identifiers you provided
                    • Advertising records linked to your Advertising ID, through Google AdMob's deletion processes

                    <strong>Third-party processors:</strong>
                    • Google AdMob: data is handled under Google's retention and deletion policies
                    • Platform store billing: purchase history is managed in your Google / Apple account
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Data That Must Be Retained / 보존되는 데이터</SectionTitle>
                <Paragraph>
                    We are legally required to retain certain records:

                    <strong>Korean Electronic Commerce Act / 전자상거래법:</strong>
                    • Contract & withdrawal records: 5 years
                    • Payment & supply records: 5 years
                    • Consumer complaints or dispute records: 3 years

                    <strong>Tax law / 세법:</strong>
                    • Tax-related records: as required by the applicable tax authority
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Effect of Deletion / 삭제 후 영향</SectionTitle>
                <Paragraph>
                    • Deleted game progress cannot be recovered. We have no server copy.
                    • Unspent platinum from consumable purchases is lost and cannot be restored.
                    • Permanent purchases (full version, class and act unlocks, ad removal, battle speed options) can be restored on the same store account via "Restore Purchases" or after reinstalling.

                    삭제 후: 게임 진행은 복구할 수 없으며(서버 사본 없음), 사용하지 않은 백금은 복원되지 않습니다. 영구 구매 상품은 같은 스토어 계정으로 복원할 수 있습니다.
                </Paragraph>
            </Section>

            <ContactBox>
                <SectionTitle>Customer Support / 고객 지원</SectionTitle>
                <Paragraph>
                    • Email: mind-wellness@naver.com
                    • Response time: within 48 business hours
                    • Business hours: Mon–Fri 09:00–18:00 KST
                </Paragraph>
            </ContactBox>

            <InfoBox>
                <SectionTitle>Document Information / 문서 정보</SectionTitle>
                <Paragraph>
                    • Originally published / 최초 작성일: 2026-10-13
                    • Last updated / 최종 수정일: 2026-10-13
                    • Version / 버전: 1.0
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default CryptCrawlerAccountDeletion;
